/* eslint-disable jsx-a11y/anchor-is-valid */
// @flow
import React, { useState, useEffect } from 'react';
// components
import Table from '../../components/Table';
import { useTranslation } from 'react-i18next';
import { Loading } from 'react-loading-ui';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { getTelemetryLive, getUserRequestAPI } from '../../API/RDPMS_API';
import moment from 'moment';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'material-design-icons/iconfont/material-icons.css';
import '../../../src/assets/scss/custom/components/_alert.scss';
import '../dashboard/Dashboard/Dashboard.css';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import {
    mapRequestDataToOptions,
    sizePerPageList,
    TelemetryLiveColumns,
    buildGateMergedLiveRowData,
} from './requestDataMapper';
import GraphChartComponent from '../../components/Charts/GraphChartComponent';
import CircuitPage from '../../components/Charts/CircuitPage';
import TelemetryGraphComponent from '../../components/Charts/TelemetryGraphComponent';

const parseGraphTime = (t) => {
    const tMoment = moment(t, 'YYYYMMDDTHHmmss,SSS');
    return tMoment.isValid() ? tMoment.valueOf() : null;
};

const formatColumnName = (name) => {
    if (!name) return '';
    if (/[\s/]/.test(name)) return name;

    return name
        .replace(/^ips_/i, '')
        .split('_')
        .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(' ');
};

const buildGraphChartData = (rawData, assetType, assetNumber) => {
    if (!assetType || !assetNumber) return { categories: [], series: [] };

    const filtered = rawData.filter((e) => e.assetType === assetType.value && e.assetNumber === assetNumber.value);

    const categories = [...new Set(filtered.map((e) => e.time))].sort((a, b) => a - b);
    const paramNames = [...new Set(filtered.map((e) => e.paramName))];

    const formatParamName = (name) => name.split('_').join(' ').toUpperCase();

    const series = paramNames.map((param) => ({
        name: formatParamName(param),
        data: categories.map((cat) => {
            const point = filtered.find((e) => e.paramName === param && e.time === cat);
            return point ? point.value : null;
        }),
    }));

    return { categories, series };
};

// ---- NEW: ELC/MLC gate params ko merged columns me dikhane ke liye (Live: single-row 'parameters' array) ----
const GATE_MERGE_ASSET_TYPES = ['ELC', 'MLC'];

const GATE_PAIR_CONFIGS = [
    {
        keys: ['gate_open', 'gate_closed'],
        label: 'Gate Open/Closed',
        display: {
            gate_open: { true: 'Open', false: 'Closed' },
            gate_closed: { true: 'Closed', false: 'Open' },
        },
    },
    {
        keys: ['gate_locked', 'gate_unlocked'],
        label: 'Gate Locked/Unlocked',
        display: {
            gate_locked: { true: 'Locked', false: 'Unlocked' },
            gate_unlocked: { true: 'Unlocked', false: 'Locked' },
        },
    },
    {
        keys: ['gate_opening', 'gate_closing'],
        label: 'Gate Opening/Closing',
        display: {
            gate_opening: { true: 'Opening', false: 'Closing' },
            gate_closing: { true: 'Closing', false: 'Opening' },
        },
    },
];

// boolean values JSX me directly render nahi hote (blank dikhte hai), isliye string me convert karna zaroori
const formatParamValue = (val) => {
    if (typeof val === 'boolean') return val ? 'True' : 'False';
    if (val === undefined || val === null || val === '') return '-';
    return val;
};

// Live modal me rowData.parameters = [{ name, value }, ...] hota hai (single row, timestamp nahi)
const buildGateMergedRowData = (rowData) => {
    if (!rowData || !GATE_MERGE_ASSET_TYPES.includes(rowData.assetType)) {
        return rowData;
    }

    const originalParams = rowData.parameters || [];
    const paramMap = new Map(originalParams.map((p) => [p.name, p.value]));
    const consumedKeys = new Set();
    const mergedParameters = [];

    originalParams.forEach((p) => {
        if (consumedKeys.has(p.name)) return;

        const pairConfig = GATE_PAIR_CONFIGS.find((cfg) => cfg.keys.includes(p.name));

        if (pairConfig) {
            pairConfig.keys.forEach((k) => consumedKeys.add(k));

            let mergedValue = '-';
            for (const key of pairConfig.keys) {
                if (paramMap.has(key)) {
                    const rawVal = paramMap.get(key);
                    mergedValue = pairConfig.display[key][rawVal] ?? rawVal;
                    break;
                }
            }

            mergedParameters.push({ name: pairConfig.label, value: mergedValue });
        } else {
            consumedKeys.add(p.name);
            mergedParameters.push({ name: p.name, value: formatParamValue(p.value) });
        }
    });

    return {
        ...rowData,
        parameters: mergedParameters,
    };
};
// ---- END NEW ----

const TelemetryLive = () => {
    const { auth } = useSelector((state) => ({
        auth: state.Auth,
    }));
    const userRole = auth?.user?.role;

    const [data, setData] = useState([]);
    const [limit, setLimit] = useState(5);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // ---- GRAPH RELATED STATE (Current + Voltage) ----
    const [rawCurrentGraphData, setRawCurrentGraphData] = useState([]);
    const [rawVoltageGraphData, setRawVoltageGraphData] = useState([]);
    const [currentGraphData, setCurrentGraphData] = useState({ categories: [], series: [] });
    const [voltageGraphData, setVoltageGraphData] = useState({ categories: [], series: [] });
    const [graphAssetTypeOptions, setGraphAssetTypeOptions] = useState([]);
    const [selectedGraphAssetType, setSelectedGraphAssetType] = useState(null);
    const [graphAssetNumberOptions, setGraphAssetNumberOptions] = useState([]);
    const [selectedGraphAssetNumber, setSelectedGraphAssetNumber] = useState(null);
    // ---- END GRAPH RELATED STATE ----

    const [circuit, setCircuit] = useState([]);
    const [circuitAssetTypeOptions, setCircuitAssetTypeOptions] = useState([]);
    const [selectedCircuitAssetType, setSelectedCircuitAssetType] = useState(null);
    const [circuitAssetNumberOptions, setCircuitAssetNumberOptions] = useState([]);
    const [selectedCircuitAssetNumber, setSelectedCircuitAssetNumber] = useState(null);

    const [selectedZones, setSelectedZones] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedDivisions, setSelectedDivisions] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedStation, setSelectedStation] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedAssetsType, setSelectedAssetsType] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedAssetsNumber, setSelectedAssetsNumber] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);

    const [selectedViews, setSelectedViews] = useState({ label: 'Table', value: 'table' });

    const [stations, setStations] = useState([]);
    const [zones, setZones] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [assetsTypeCode, setAssetsTypeCode] = useState([]);
    const [assetsType, setAssetsType] = useState([]);
    const [assetsNumber, setAssetsNumber] = useState([]);

    const [statusMessage, setStatusMessage] = useState('');

    // ---- STATE FOR PARAMETER DETAIL MODAL ----
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [rowDetailsList, setRowDetailsList] = useState([]);
    // ---- END STATE ----

    useEffect(() => {
        const fetchStation = async () => {
            await getRequestData(auth.user.id);
        };

        fetchStation();
    }, []);

    useEffect(() => {
        if (stations && stations.length > 0) {
            getTelemetryLiveData(limit, pageNumber);
        }
    }, [stations]);

    // Selected asset type ke basis pe asset number options nikalna — current + voltage ka union
    useEffect(() => {
        if (!selectedGraphAssetType) {
            setGraphAssetNumberOptions([]);
            setSelectedGraphAssetNumber(null);
            return;
        }

        const numbers = [
            ...new Set(
                [...rawCurrentGraphData, ...rawVoltageGraphData]
                    .filter((e) => e.assetType === selectedGraphAssetType.value)
                    .map((e) => e.assetNumber)
            ),
        ].map((n) => ({ label: n, value: n }));

        setGraphAssetNumberOptions(numbers);
        setSelectedGraphAssetNumber(numbers[0] || null);
    }, [selectedGraphAssetType, rawCurrentGraphData, rawVoltageGraphData]);

    // Current graph chart data build karna
    useEffect(() => {
        setCurrentGraphData(buildGraphChartData(rawCurrentGraphData, selectedGraphAssetType, selectedGraphAssetNumber));
    }, [selectedGraphAssetNumber, selectedGraphAssetType, rawCurrentGraphData]);

    // Voltage graph chart data build karna
    useEffect(() => {
        setVoltageGraphData(buildGraphChartData(rawVoltageGraphData, selectedGraphAssetType, selectedGraphAssetNumber));
    }, [selectedGraphAssetNumber, selectedGraphAssetType, rawVoltageGraphData]);

    useEffect(() => {
        if (!selectedCircuitAssetType) {
            setCircuitAssetNumberOptions([]);
            setSelectedCircuitAssetNumber(null);
            return;
        }

        const numbers = [
            ...new Set(circuit.filter((c) => c.assetType === selectedCircuitAssetType.value).map((c) => c.assetNumber)),
        ].map((n) => ({ label: n, value: n }));

        setCircuitAssetNumberOptions(numbers);
        setSelectedCircuitAssetNumber(numbers[0] || null);
    }, [selectedCircuitAssetType, circuit]);

    const { t } = useTranslation();

    const handleShowParamModal = (rowInfo) => {
        setSelectedRowData(rowInfo);
    };
    const handleTableRowClick = (e) => {
        const trElem = e.target.closest('tr');
        if (!trElem) return;

        const tbodyElem = trElem.closest('tbody');
        if (!tbodyElem) return;

        const rowIndex = Array.from(tbodyElem.children).indexOf(trElem);
        if (rowIndex === -1) return;

        const rowInfo = rowDetailsList[rowIndex];
        if (rowInfo) {
            setSelectedRowData(rowInfo);
        }
    };

    const getTelemetryLiveData = async (limits, pages) => {
        Loading();
        let limitNumber = limits ? limits : limit;
        let pagesNumber = pages ? pages : pageNumber;

        const zone = selectedZones.some((z) => z.value === 'all') ? [] : selectedZones.map((z) => z.value);

        const division = selectedDivisions.some((d) => d.value === 'all') ? [] : selectedDivisions.map((d) => d.value);

        let station = [];
        if (userRole === 'super admin') {
            station = selectedStation.some((s) => s.value === 'all') ? [] : selectedStation.map((s) => s.value);
        } else {
            if (selectedStation.length > 0 && !selectedStation.some((s) => s.value === 'all')) {
                station = selectedStation.map((s) => s.value);
            } else {
                station = stations.filter((s) => s.value !== 'all').map((s) => s.value);
            }
        }

        const assetsType = selectedAssetsType.some((a) => a.value === 'all')
            ? []
            : selectedAssetsType.map((a) => a.value);

        const assetsNumber = selectedAssetsNumber.some((item) => item.value === 'all')
            ? []
            : selectedAssetsNumber.map((item) => ({
                  sc: item.sc,
                  asset_number_code: item.label,
              }));

        let request = {
            zone: zone,
            division: division,
            station: station,
            asset_type: assetsType,
            asset_number: assetsNumber,

            page_number: pagesNumber,
            page_size: limitNumber,
        };

        let response = await getTelemetryLive(request);
        // console.log('Telemetry Live Response:', response);
        if (response) {
            let srNo = (pageNumber - 1) * limitNumber + 1;
            let dataArray = [];
            let rowDetailsArray = [];

            response?.zone?.forEach((z) => {
                z.division?.forEach((d) => {
                    d.station?.forEach((s) => {
                        s.asset_type?.forEach((at) => {
                            at.asset_number?.forEach((asn) => {
                                const paramKeys = Object.keys(asn).filter(
                                    (key) => key !== 'asnc' && key !== 'parameterCode'
                                );

                                const firstReading = paramKeys.map((key) => asn[key]?.prt?.[0]).find(Boolean);
                                const presentTimeMoment = firstReading
                                    ? moment(firstReading, 'YYYYMMDDTHHmmss,SSS')
                                    : null;
                                const presentTime = presentTimeMoment?.isValid()
                                    ? presentTimeMoment.format('DD/MM/YYYY HH:mm:ss')
                                    : firstReading || '';

                                const rowInfoForModal = {
                                    zoneCode: z.zc || '',
                                    divisionCode: d.dc || '',
                                    stationCode: s.sc || '',
                                    assetType: at.astc || '',
                                    assetNumber: asn.asnc || '',
                                    presentTime,
                                    parameters: paramKeys.map((key) => ({
                                        name: key,
                                        value: asn[key]?.prv?.[0],
                                    })),
                                };

                                rowDetailsArray.push(rowInfoForModal);

                                dataArray.push({
                                    srNo: srNo++,
                                    vcc: response?.vcc || '',
                                    vcn: response?.vcn || '',
                                    zoneCode: z.zc || '',
                                    divisionCode: d.dc || '',
                                    stationCode: s.sc || '',
                                    assetType: at.astc || '',
                                    assetNumber: asn.asnc || '',
                                    presentTime,

                                    action: (
                                        <button
                                            type="button"
                                            className="btn btn-sm p-0"
                                            data-bs-toggle="modal"
                                            data-bs-target="#parameterDetailModal"
                                            onClick={() => handleShowParamModal(rowInfoForModal)}
                                            title="View Parameters"
                                            style={{ border: 'none', background: 'transparent' }}>
                                            <i className="mdi mdi-eye"></i>
                                        </button>
                                    ),
                                });
                            });
                        });
                    });
                });
            });

            setPageNumber(response?.pagination?.currentPage);
            setPageCount(response?.pagination?.totalPages);
            setTotalCount(response?.pagination?.totalCount);
            setData(dataArray);
            setRowDetailsList(rowDetailsArray);

            setCircuit(response?.circuitRows || []);

            const circuitTypeOptions = [...new Set((response?.circuitRows || []).map((c) => c.assetType))].map((t) => ({
                label: t,
                value: t,
            }));
            setCircuitAssetTypeOptions(circuitTypeOptions);
            setSelectedCircuitAssetType(circuitTypeOptions[0] || null);

            const currentGraphEntries = (response?.currentGraphRows || [])
                .map((e) => ({ ...e, time: parseGraphTime(e.time) }))
                .filter((e) => e.time !== null);

            const voltageGraphEntries = (response?.voltageGraphRows || [])
                .map((e) => ({ ...e, time: parseGraphTime(e.time) }))
                .filter((e) => e.time !== null);

            setRawCurrentGraphData(currentGraphEntries);
            setRawVoltageGraphData(voltageGraphEntries);

            const assetTypeOptions = [
                ...new Set([...currentGraphEntries, ...voltageGraphEntries].map((e) => e.assetType)),
            ].map((t) => ({
                label: t,
                value: t,
            }));
            setGraphAssetTypeOptions(assetTypeOptions);
            setSelectedGraphAssetType(assetTypeOptions[0] || null);

            setStatusMessage('Alerts telemetry history fetched successfully');
        }
        Loading();
    };

    const getRequestData = async (id) => {
        try {
            Loading(true);

            const response = await getUserRequestAPI(id);

            if (response) {
                const { stations, zones, divisions, assetsType, assetsTypeCode, assetsNumber } =
                    mapRequestDataToOptions(response);

                setStations(stations);
                setZones(zones);
                setDivisions(divisions);
                setAssetsType(assetsType);
                setAssetsTypeCode(assetsTypeCode);
                setAssetsNumber(assetsNumber);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            Loading(false);
        }
    };

    const handlePageChange = async (value) => {
        setPageNumber(1);
        setLimit(value);
        await getTelemetryLiveData(value, 1);
    };

    const handlePageClick = async (value) => {
        setPageNumber(value);
        await getTelemetryLiveData(limit, value);
    };

    const viewOptions = [
        { label: 'Table', value: 'table' },
        { label: 'Graph', value: 'graph' },
        { label: 'Circuit', value: 'circuit' },
    ];

    return (
        <>
            <ToastContainer />

            <div className=" py-1 " style={{ width: '100%' }}>
                <div className="container-fluid px-0">
                    <div className="row align-items-center gx-2 gy-2">
                        <div className="col-12 col-md-auto">
                            <h5
                                className="fw-bold mb-1"
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    color: '#000',
                                    paddingLeft: '10px',
                                }}>
                                Telemetry Live
                            </h5>
                        </div>
                    </div>
                </div>

                <div
                    className="report-alert-card my-2 p-1"
                    style={{
                        width: '100%',
                        boxShadow: '0px 0px 35px 0px rgba(154, 161, 171, 0.15)',
                        position: 'relative',
                        height: 'auto',
                    }}>
                    <div
                        className="container-fluid"
                        style={{
                            paddingLeft: '10px',
                            paddingRight: '10px',
                        }}>
                        <form>
                            <div className="row d-flex gx-3 gy-2 align-items-baseline">
                                <MultiSelectDropdown
                                    label="Zones"
                                    id="zoneSelect"
                                    options={zones}
                                    selectedValues={selectedZones}
                                    onChange={setSelectedZones}
                                />

                                <MultiSelectDropdown
                                    label="Divisions"
                                    id="divisionSelect"
                                    options={divisions}
                                    selectedValues={selectedDivisions}
                                    onChange={setSelectedDivisions}
                                />

                                <MultiSelectDropdown
                                    label="Station"
                                    id="stationSelect"
                                    options={stations}
                                    selectedValues={selectedStation}
                                    onChange={setSelectedStation}
                                />

                                <MultiSelectDropdown
                                    label="Assets Type"
                                    id="assetsSelect"
                                    options={assetsType}
                                    selectedValues={selectedAssetsType}
                                    onChange={setSelectedAssetsType}
                                />

                                <MultiSelectDropdown
                                    label="Assets Number"
                                    id="assetsNumberSelect"
                                    options={assetsNumber}
                                    selectedValues={selectedAssetsNumber}
                                    onChange={setSelectedAssetsNumber}
                                />

                                <MultiSelectDropdown
                                    label="View"
                                    id="viewSelect"
                                    options={viewOptions}
                                    selectedValues={selectedViews}
                                    onChange={setSelectedViews}
                                    isMulti={false}
                                />

                                <div className="col-auto ms-auto d-flex flex-column">
                                    <label style={{ visibility: 'hidden', marginBottom: '0.25rem' }}>Submit</label>
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => getTelemetryLiveData(5, 1)}
                                        style={{
                                            backgroundColor: '#F9B74C',
                                            color: 'black',
                                            fontWeight: 'bold',
                                            width: '150px',
                                            textTransform: 'uppercase',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: '14px',
                                        }}>
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {(!selectedViews || selectedViews.value === 'table') && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0  text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <style>{`
                                .telemetry-clickable-rows tbody tr {
                                    cursor: pointer;
                                }
                                .telemetry-clickable-rows tbody tr:hover {
                                    background-color: #eff5ff;
                                }
                            `}</style>
                        <div className="telemetry-clickable-rows" onClick={handleTableRowClick}>
                            <Table
                                columns={TelemetryLiveColumns}
                                data={data}
                                pageSize={limit}
                                pageCount={pageCount}
                                pageNumber={pageNumber}
                                totalCount={totalCount}
                                sizePerPageList={sizePerPageList}
                                isSortable={true}
                                pagination={true}
                                isSearchable={true}
                                onPageChange={handlePageChange}
                                onPageClick={handlePageClick}
                            />
                        </div>
                    </div>
                )}

                {selectedViews?.value === 'graph' && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0 text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <div className="row mb-2 gx-2">
                            <MultiSelectDropdown
                                label="Assets Type"
                                options={graphAssetTypeOptions}
                                selectedValues={selectedGraphAssetType}
                                onChange={setSelectedGraphAssetType}
                                isMulti={false}
                            />
                            <MultiSelectDropdown
                                label="Assets Number"
                                options={graphAssetNumberOptions}
                                selectedValues={selectedGraphAssetNumber}
                                onChange={setSelectedGraphAssetNumber}
                                isMulti={false}
                            />
                        </div>

                        <h6
                            className="fw-bold mt-2 mb-1"
                            style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                            Current
                        </h6>
                        <TelemetryGraphComponent
                            categories={currentGraphData?.categories}
                            series={currentGraphData?.series}
                        />

                        <h6
                            className="fw-bold mt-4 mb-1"
                            style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                            Voltage
                        </h6>
                        <TelemetryGraphComponent
                            categories={voltageGraphData?.categories}
                            series={voltageGraphData?.series}
                        />
                    </div>
                )}

                {selectedViews?.value === 'circuit' && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0  text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <div className="row mb-2 gx-2">
                            <MultiSelectDropdown
                                label="Assets Type"
                                options={circuitAssetTypeOptions}
                                selectedValues={selectedCircuitAssetType}
                                onChange={setSelectedCircuitAssetType}
                                isMulti={false}
                            />
                            <MultiSelectDropdown
                                label="Assets Number"
                                options={circuitAssetNumberOptions}
                                selectedValues={selectedCircuitAssetNumber}
                                onChange={setSelectedCircuitAssetNumber}
                                isMulti={false}
                            />
                        </div>

                        <CircuitPage
                            circuit={circuit.filter(
                                (c) =>
                                    c.assetType === selectedCircuitAssetType?.value &&
                                    c.assetNumber === selectedCircuitAssetNumber?.value
                            )}
                        />
                    </div>
                )}
            </div>

            <div className="modal fade" id="statusModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-2">{statusMessage}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- PARAMETER DETAIL MODAL (Live: single row, gate params merged for ELC/MLC) ---- */}
            <div className="modal fade" id="parameterDetailModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header" style={{ borderBottom: 'none' }}>
                            <h5
                                className="modal-title fw-bold"
                                style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                                Parameter Details
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {selectedRowData &&
                                (() => {
                                    const displayRowData = buildGateMergedLiveRowData(selectedRowData);
                                    return (
                                        <>
                                            <div className="row gx-2 gy-2 mb-3">
                                                {[
                                                    { label: 'Zone', value: selectedRowData.zoneCode },
                                                    { label: 'Division', value: selectedRowData.divisionCode },
                                                    { label: 'Station', value: selectedRowData.stationCode },
                                                    { label: 'Asset Type', value: selectedRowData.assetType },
                                                    { label: 'Asset Number', value: selectedRowData.assetNumber },
                                                    { label: 'Present Time', value: selectedRowData.presentTime },
                                                ].map((info, idx) => (
                                                    <div className="col-6 col-md-4 col-lg-2" key={idx}>
                                                        <div
                                                            style={{
                                                                border: '1px solid #eee',
                                                                borderRadius: '8px',
                                                                padding: '8px 10px',
                                                                height: '100%',
                                                                backgroundColor: '#fafafa',
                                                            }}>
                                                            <div
                                                                style={{
                                                                    fontSize: '11px',
                                                                    color: '#888',
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 600,
                                                                    letterSpacing: '0.4px',
                                                                }}>
                                                                {info.label}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: '14px',
                                                                    color: '#000',
                                                                    fontWeight: 600,
                                                                    wordBreak: 'break-word',
                                                                    fontFamily: "'Poppins', sans-serif",
                                                                }}>
                                                                {info.value || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div
                                                style={{
                                                    overflowX: 'auto',
                                                    width: '100%',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '6px',
                                                }}>
                                                <table
                                                    className="table table-bordered text-center mb-0"
                                                    style={{ minWidth: 'max-content' }}>
                                                    <thead>
                                                        <tr>
                                                            {displayRowData.parameters?.map((p, idx) => (
                                                                <th
                                                                    key={idx}
                                                                    style={{
                                                                        backgroundColor: '#eff5ff',
                                                                        color: '#000',
                                                                        fontWeight: 700,
                                                                        whiteSpace: 'nowrap',
                                                                        border: '1px solid #dee2e6',
                                                                        padding: '8px 14px',
                                                                        fontFamily: "'Poppins', sans-serif",
                                                                        position: 'sticky',
                                                                        top: 0,
                                                                    }}>
                                                                    {formatColumnName(p.name)}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr>
                                                            {displayRowData.parameters?.map((p, idx) => (
                                                                <td
                                                                    key={idx}
                                                                    style={{
                                                                        whiteSpace: 'nowrap',
                                                                        border: '1px solid #dee2e6',
                                                                        padding: '8px 14px',
                                                                    }}>
                                                                    {p.value}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    );
                                })()}
                        </div>
                    </div>
                </div>
            </div>
            {/* ---- END MODAL ---- */}
        </>
    );
};

export default TelemetryLive;
